.makers {
  background: var(--color-primary-grey-000); /* PRIMARY_GREY_000 */
  padding: 76px 0;
}

.makers__head {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 48px;
  margin-bottom: 52px;
}

.makers__title {
  font-family: var(--plume-font-display, sofia-pro, sans-serif);
  font-weight: 900;
  font-size: clamp(30px, 4vw, 50px);
  line-height: 1.04;
  letter-spacing: -0.02em;
  color: var(--color-primary-grey-900); /* PRIMARY_GREY_900 */
  margin: 0 0 16px;
}

.makers__title em {
  font-style: italic;
  font-weight: 900;
  color: var(--color-primary-red-500); /* PRIMARY_RED_500 */
}

.makers__lede {
  font-size: 18px;
  line-height: 1.5;
  color: var(--color-primary-sand-900); /* text-2 */
  max-width: 560px;
  margin: 0;
}

.makers__portrait {
  width: 254px;
  /* centres the owl when it stacks full-width above the text on mobile; a no-op in the desktop
     `auto` grid track, which sizes to exactly 254px. */
  margin-inline: auto;
}

/* ---------- Value cards (Plume sticker style) ---------- */
.values {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 22px;
}

.value {
  --vc: var(--color-primary-grey-900); /* sticker shadow colour — ink by default; dark/mint recolour it */
  min-width: 0; /* let cards shrink below content so the grid can't blow out on narrow screens */
  border: var(--plume-border-card, 2px solid var(--color-primary-grey-900));
  border-radius: 16px;
  padding: 30px 26px;
  box-shadow: 6px 6px 0 var(--vc);
  transform: rotate(var(--rot, 0deg));
  transition:
    transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 0.25s cubic-bezier(0.04, 0.4, 0.5, 0.95);
}

.value:hover {
  transform: rotate(0deg) translateY(-3px);
  box-shadow: 9px 9px 0 var(--vc);
}

.value__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 13px;
  margin-bottom: 18px;
  /* Same neutral chip + black glyph on every card (per-variant accents read too weak on the light fills). */
  background: rgba(35, 34, 33, 0.12); /* PRIMARY_GREY_900 @ 12% */
  color: var(--color-primary-grey-900); /* PRIMARY_GREY_900 */
}

.value__icon svg {
  width: 24px;
  height: 24px;
}

.value__title {
  font-family: var(--plume-font-display, sofia-pro, sans-serif);
  font-size: 23px; /* HEADING_S */
  font-weight: 900;
  letter-spacing: -0.01em;
  line-height: 1.1;
  margin: 0 0 10px;
}

.value__body {
  font-size: 14px; /* COPY_S */
  line-height: 1.55;
  margin: 0;
}

.valueYellow {
  background: var(--color-complementary-yellow); /* COMPLEMENTARY_YELLOW */
}
.valueYellow .value__title {
  color: var(--color-primary-grey-900);
}
.valueYellow .value__body {
  color: #443f38; /* HACK: warm near-black for body text on the yellow fill — no DS token at this tint */
}

.valueBlue {
  background: var(--color-primary-blue-100); /* blue-100 */
}
.valueBlue .value__title {
  color: var(--color-primary-grey-900); /* PRIMARY_GREY_900 */
}
.valueBlue .value__body {
  color: var(--color-primary-grey-900); /* PRIMARY_GREY_900 */
}

.valuePink {
  /* shadow (--vc) stays the default ink var(--color-primary-grey-900) (PRIMARY_GREY_900) — "our black" */
  background: var(--color-primary-red-200); /* PRIMARY_RED_200 */
}
.valuePink .value__title {
  color: var(--color-primary-grey-900); /* PRIMARY_GREY_900 */
}
.valuePink .value__body {
  color: var(--color-primary-grey-900); /* PRIMARY_GREY_900 */
}

.valueMint {
  --vc: var(--color-secondary-green-800); /* SECONDARY_GREEN_800 */
  background: var(--color-secondary-green-200); /* success-100 */
}
.valueMint .value__title {
  color: var(--color-primary-grey-900);
}
.valueMint .value__body {
  color: var(--color-primary-grey-900); /* PRIMARY_GREY_900 */
}

/* Placement of the shared Trustpilot row; its own look lives in `_shared/Trustpilot`. */
.trust {
  margin-top: 48px;
}

@media (max-width: 880px) {
  /* Stacked layout: turn the section body into a flex column and dissolve the head grid
     (display: contents) so the heading and the owl become direct items of that column. This lets
     us reorder the owl to sit right above the Mission card — text → value cards → Trustpilot → owl →
     Mission — instead of at the top by the heading. Mission keeps its default order (0) so it stays
     last with its own margin-top. */
  .makers__stack {
    display: flex;
    flex-direction: column;
  }

  .makers__head {
    display: contents;
  }

  .makers__intro {
    order: -4;
    margin-bottom: 40px; /* replaces the head's dropped margin-bottom (its box is gone) */
  }

  .values {
    order: -3;
  }

  .trust {
    order: -2;
  }

  .makers__portrait {
    order: -1;
    margin-top: 48px; /* gap from the Trustpilot row above (Mission supplies the gap below) */
  }
}

@media (max-width: 560px) {
  .values {
    grid-template-columns: 1fr;
  }
}
